import React from 'react';
import { Share2, Star } from 'lucide-react';
import i18n from '../../../../../i18n';

// The bank-account shape differs slightly depending on where it comes from:
// the accounts list (`account_number`, `branch_name`, `is_default`) vs. the
// `organization_bank_account` relation returned by the payment config API
// (`account_name`, `iban`, `is_default`). This normalizes either shape down
// to what the preview needs to render.
const normalizeAccount = (account) => {
  if (!account) return null;

  const identifier = account.iban || account.account_number;

  return {
    bankName: account.bank_name || null,
    accountName: account.account_name || null,
    iban: account.iban || null,
    maskedNumber: account.account_number ? `****${String(account.account_number).slice(-4)}` : null,
    last4: identifier ? String(identifier).replace(/\s+/g, '').slice(-4) : null,
    isPrimary: Boolean(account.is_default),
  };
};

const formatIban = (iban) => {
  if (!iban) return null;
  // group into 4-character chunks for readability, e.g. OM36 0270 4400 ...
  return String(iban)
    .replace(/\s+/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
};

const BranchSkeleton = ({ label }) => (
  <div className="smart-routing-preview__branch">
    <span className="smart-routing-preview__label">{label}</span>
    <span className="smart-routing-preview__connector" />
    <div className="smart-routing-preview__result smart-routing-preview__result--skeleton">
      <span className="smart-routing-preview__result-skeleton-line" />
      <span className="smart-routing-preview__result-skeleton-line smart-routing-preview__result-skeleton-line--sm" />
    </div>
  </div>
);

// Bank details are shown for both Mzadcom and Client destinations — the
// settlement account is public information on the bidder side either way,
// so there's no reason to hide it for the Mzadcom branch.
const BranchResult = ({ label, source, accounts }) => {
  const normalized = (accounts || []).map(normalizeAccount).filter(Boolean);
  const isClient = source === 'organization';

  return (
    <div className="smart-routing-preview__branch">
      <span className="smart-routing-preview__label">{label}</span>
      <span className="smart-routing-preview__connector" />

      <div className="smart-routing-preview__result">
        <span className="smart-routing-preview__result-title">
          {isClient ? i18n.t('client_account') : i18n.t('mzadcom_account')}
        </span>

        {normalized.length === 0 && (
          <span className="smart-routing-preview__result-subtitle">{i18n.t('no_account_selected')}</span>
        )}

        {normalized.length === 1 && (
          <div className="smart-routing-preview__bank-detail">
            <div className="smart-routing-preview__bank-detail-row">
              <span className="smart-routing-preview__bank-detail-name">{normalized[0].bankName}</span>
              {normalized[0].isPrimary && (
                <span className="smart-routing-preview__bank-detail-badge">
                  <Star size={11} fill="#198754" />
                  {i18n.t('primary')}
                </span>
              )}
            </div>
            {normalized[0].accountName && (
              <p className="smart-routing-preview__bank-detail-line">{normalized[0].accountName}</p>
            )}
            {(normalized[0].iban || normalized[0].maskedNumber) && (
              <p className="smart-routing-preview__bank-detail-iban">
                {normalized[0].iban ? formatIban(normalized[0].iban) : normalized[0].maskedNumber}
              </p>
            )}
          </div>
        )}

        {normalized.length > 1 && (
          <div className="smart-routing-preview__bank-detail">
            <span className="smart-routing-preview__result-subtitle">
              {i18n.t('split_between_accounts', { count: normalized.length })}
            </span>
            <div className="smart-routing-preview__bank-detail-list">
              {normalized.map((account, index) => (
                <div className="smart-routing-preview__bank-detail-chip" key={index}>
                  <span className="smart-routing-preview__bank-detail-chip-name">{account.bankName}</span>
                  {(account.iban || account.maskedNumber) && (
                    <span className="smart-routing-preview__bank-detail-chip-number">
                      {account.iban ? `••••${account.last4}` : account.maskedNumber}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// `enrollmentAccount` / `paymentAccounts` are optional — when the config API
// hasn't resolved a bank account relation yet they're simply ignored and the
// "no account selected" fallback renders instead.
const SmartRoutingPreview = ({
  isLoading,
  enrollmentSource,
  paymentDestination,
  enrollmentAccount,
  paymentAccounts,
}) => {
  return (
    <div className="smart-routing-preview">
      <div className="smart-routing-preview__header">
        <span className="smart-routing-preview__icon">
          <Share2 size={20} />
        </span>
        <div>
          <p className="smart-routing-preview__title">{i18n.t('smart_routing_preview')}</p>
          <p className="smart-routing-preview__subtitle">{i18n.t('smart_routing_preview_subtitle')}</p>
        </div>
      </div>

      <div className="smart-routing-preview__diagram">
        {isLoading ? (
          <React.Fragment>
            <BranchSkeleton label={i18n.t('auction_enrollment')} />
            <BranchSkeleton label={i18n.t('auction_payment')} />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <BranchResult
              label={i18n.t('auction_enrollment')}
              source={enrollmentSource}
              accounts={enrollmentAccount ? [enrollmentAccount] : []}
            />
            <BranchResult label={i18n.t('auction_payment')} source={paymentDestination} accounts={paymentAccounts} />
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default SmartRoutingPreview;
